// R5.3 (round 2) — THE TERMINAL FACT: **LAST WINS**, in ONE implementation, shared by every reader of `facts[]`.
//
// `entry.facts[]` IS AN APPEND-ONLY AUDIT TRAIL. Codebook §2.8: "edits are stored with an audit trail and never
// silently overwritten", and the PATCH route implements that literally — an edit APPENDS a `user_supplied` fact
// after the `extracted_span` one it replaces, and the original stays in the array forever as the `replaces_span`
// the surface can offer back on request.
//
// SO "THE FACT FOR THIS FIELD" IS THE **LAST** ONE, NOT THE FIRST. That sounds obvious written down, and the
// R5.3 build review found the product disagreeing with itself about it:
//
//   * the PATCH route resolved `replaces_span` with `[...facts].reverse().find(...)`  — LAST wins;
//   * the surface's Layer 1 collapsed rows last-wins                                   — LAST wins;
//   * the rubric ENGINE resolved a field's evidencing fact with `facts.find(...)`      — FIRST wins.
//
// The consequence was not cosmetic. After a correction, the engine could compute a band from the applicant's
// REPLACEMENT value (which is what the mutation writes onto the entry's column) while stamping the trace with the
// SUPERSEDED extracted fact's id — so the assessment and its evidence disagreed, and "show source" opened onto
// the very sentence the applicant had just told us was wrong. That is a C2a defect: the claim is evidenced by
// something that does not support it any more.
//
// THIS MODULE IS THEREFORE A BEHAVIOURAL ENGINE FIX, not a refactor, and it is why `SCORING_VERSION` moves 5 → 6
// (`rubricEngine.ts`): an identical ledger with an edit in it now yields different evidence ids than it did
// before, which is exactly the condition that constant exists to record.
//
// PURE, NO IMPORTS, STRUCTURALLY TYPED — so the worker's engine, the worker's PATCH route and the client's panel
// can all call the SAME function rather than each keeping an opinion about it.

/** All this resolver needs to know about a fact. Anything else on the object is carried through untouched. */
export interface FieldedFact {
  field: string;
}

/**
 * The CURRENT fact for one field — the last one appended, or `null` when the field has no fact at all.
 *
 * `null` rather than a synthesized id: a rule that needs evidence it does not have MUST NOT FIRE, and the engine's
 * `factIdFor` documents at length why the id-shaped fallback it used to return was worse than nothing.
 */
export function terminalFact<T extends FieldedFact>(
  facts: readonly T[] | null | undefined,
  field: string,
): T | null {
  if (!Array.isArray(facts)) return null;
  for (let i = facts.length - 1; i >= 0; i -= 1) {
    const f = facts[i];
    if (f && f.field === field) return f;
  }
  return null;
}

/**
 * One fact per field — the terminal one — in FIRST-APPEARANCE order of the field.
 *
 * The two halves are both deliberate. LAST-WINS decides WHICH fact represents a field (the audit trail's head).
 * FIRST-APPEARANCE decides WHERE that field sits among the others, so an edit does not silently reorder the
 * ledger card under the applicant's cursor — §4.1's "document order" is a property of the entry, not of when the
 * applicant last touched a row.
 */
export function terminalFactsByField<T extends FieldedFact>(facts: readonly T[] | null | undefined): T[] {
  if (!Array.isArray(facts)) return [];
  const order: string[] = [];
  const byField = new Map<string, T>();
  for (const f of facts) {
    if (!f || typeof f.field !== 'string') continue;
    if (!byField.has(f.field)) order.push(f.field);
    byField.set(f.field, f);
  }
  return order.map((field) => byField.get(field)!);
}
